/**
 * Fence auto-connect.
 *
 * A fence is placed as ONE kind — `fence` (wood) or `fence_stone` — and the art is
 * chosen from which sides another fence continues through. Four sides, sixteen
 * cases, keyed the same way the coastline waterline keys are (see
 * `scripts/build-shore-tiles.py` and `shoreKeys` in the scene): the run is
 * described by its neighbours, not by the player picking a piece from a menu.
 *
 * This replaces the old manual builder, where the player chose between seven
 * variants (`fence`, `fence_v`, `fence_cnw`, …) and had to get every corner right
 * by hand. Those kinds are still accepted so existing farms keep rendering, but a
 * newly placed fence resolves its own shape, and its neighbours re-resolve theirs
 * — so drawing a pen is just tapping tiles.
 *
 * Pure and DOM-free so it can be unit-tested; the renderer only maps the returned
 * key to `sprites/buildings/fence[_<material>]<key>.png`.
 */

/** Which sides a fence continues through. */
export interface FenceNeighbours {
  n: boolean;
  s: boolean;
  e: boolean;
  w: boolean;
}

/**
 * The sprite suffix for a tile with these neighbours.
 *
 * Returns the same strings the slicer writes: `''` for the horizontal straight,
 * `_v`, `_post`, `_end_*`, `_cnw`/`_cne`/`_csw`/`_cse`, `_tn`/`_ts`/`_te`/`_tw`
 * and `_x`.
 *
 * Corner naming follows the existing files: `_cnw` is the ┌ corner (rails leaving
 * east and south), matching how the build panel labelled them.
 */
export function fenceKey({ n, s, e, w }: FenceNeighbours): string {
  const count = (n ? 1 : 0) + (s ? 1 : 0) + (e ? 1 : 0) + (w ? 1 : 0);

  if (count === 0) return '_post';
  if (count === 4) return '_x';

  if (count === 1) {
    if (n) return '_end_n';
    if (s) return '_end_s';
    if (e) return '_end_e';
    return '_end_w';
  }

  if (count === 3) {
    if (!n) return '_ts';   // ┬ — everything but north
    if (!s) return '_tn';   // ┴
    if (!e) return '_tw';   // ┤
    return '_te';           // ├
  }

  // Two sides: either a straight run or a corner.
  if (e && w) return '';     // ─
  if (n && s) return '_v';   // │
  if (e && s) return '_cnw'; // ┌
  if (w && s) return '_cne'; // ┐
  if (e && n) return '_csw'; // └
  return '_cse';             // ┘
}

/** Every fence kind, old manual variants included. */
export function isFenceKind(kind: string): boolean {
  return kind === 'fence' || kind.startsWith('fence_');
}

/**
 * The material a fence kind belongs to, as the sprite-name prefix.
 *
 * `fence`/`fence_v`/`fence_cnw` → `'fence'` (wood); `fence_stone*` → `'fence_stone'`.
 * Anything else placed in the future follows the same shape: the material is the
 * kind up to its first shape suffix.
 */
export function fenceMaterial(kind: string): string {
  return kind.startsWith('fence_stone') ? 'fence_stone' : 'fence';
}

/**
 * Resolve the sprite for a fence at (tx, ty), given a lookup of what stands where.
 *
 * Only fences of the SAME material connect: a stone run meeting a wooden one reads
 * as two fences that happen to touch, which is what it looks like on the ground.
 */
export function fenceSprite(
  kind: string,
  tx: number,
  ty: number,
  at: (tx: number, ty: number) => string | null,
): string {
  const material = fenceMaterial(kind);
  const joins = (x: number, y: number) => {
    const other = at(x, y);
    return !!other && isFenceKind(other) && fenceMaterial(other) === material;
  };
  return material + fenceKey({
    n: joins(tx, ty - 1),
    s: joins(tx, ty + 1),
    e: joins(tx + 1, ty),
    w: joins(tx - 1, ty),
  });
}
